import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Header from './components/Header';
import HeroSection from './components/HeroSection';
import AboutUs from './components/AboutUs';
import PropertiesSection from './components/PropertiesSection';
import MapSection from './components/MapSection';
import ContactSection from './components/ContactSection';
import Footer from './components/Footer';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import PropertyModal from './components/PropertyModal';
import { GlobalStyles } from './styles/GlobalStyles';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './styles/fonts.css';

const MobileOnlySection = styled.div`
  @media (min-width: 769px) {
    display: none;
  }
`;

const DesktopOnlySection = styled.div`
  @media (max-width: 768px) {
    display: none;
  }
`;

const AboutUsSection = styled.section`
  background-color: #ffffff;
  padding: 80px 0;
  margin: 0;
  width: 100%;
  max-width: 100vw;
  overflow-x: hidden;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 80px;
`;

const SectionTitle = styled.h2`
  font-family: 'Poppins', sans-serif;
  font-size: 3rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 20px;
  text-transform: uppercase;
  letter-spacing: 2px;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }

  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const ContentSection = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 80px;
  gap: 40px;

  @media (max-width: 1024px) {
    gap: 30px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 30px;
    margin-bottom: 60px;
  }

  @media (max-width: 480px) {
    gap: 20px;
    margin-bottom: 40px;
  }
`;

const TextContent = styled.div`
  flex: 1;
  background-color: #e8f5f3;
  padding: 30px 25px;
  margin: 0 -25px;
  width: 600px;
  height: auto;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  gap: 15px;
  border-radius: 20px;

  @media (max-width: 768px) {
    padding: 25px 20px;
    margin: 0 -20px;
    width: 100%;
    height: auto;
  }

  @media (max-width: 480px) {
    padding: 20px 15px;
    margin: 0 -15px;
    width: 100%;
    height: auto;
  }
`;

const ContentText = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 1.1rem;
  color: #555;
  line-height: 1.8;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.6;
    margin-bottom: 15px;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
    line-height: 1.5;
    margin-bottom: 12px;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const ImageContainer = styled.div`
  flex: 2;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  min-height: 400px;

  @media (max-width: 1024px) {
    gap: 15px;
  }

  @media (max-width: 768px) {
    display: none;
  }

  @media (max-width: 480px) {
    display: none;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  height: 200px;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }

  &:nth-child(1) {
    grid-row: span 2;
    height: 420px;

    @media (max-width: 1024px) {
      height: 350px;
    }

    @media (max-width: 768px) {
      height: 200px;
      grid-row: span 1;
    }

    @media (max-width: 480px) {
      height: 180px;
    }
  }

  @media (max-width: 768px) {
    height: 180px;
  }

  @media (max-width: 480px) {
    height: 160px;
    border-radius: 10px;
  }
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 15px;
`;

// Animation Variants
const fadeInUp = {
  hidden: { 
    opacity: 0, 
    y: 60,
    scale: 0.95
  },
  visible: { 
    opacity: 1, 
    y: 0,
    scale: 1,
    transition: {
      duration: 0.8,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

const slideInLeft = {
  hidden: { 
    opacity: 0, 
    x: -80,
    scale: 0.95
  },
  visible: { 
    opacity: 1, 
    x: 0,
    scale: 1,
    transition: {
      duration: 0.8,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

const slideInRight = {
  hidden: { 
    opacity: 0, 
    x: 80,
    scale: 0.95
  },
  visible: { 
    opacity: 1, 
    x: 0,
    scale: 1,
    transition: {
      duration: 0.8,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

// Desktop About Us Component
const DesktopAboutUs = () => {
  return (
    <AboutUsSection>
      <ContentWrapper>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.3 }}
          variants={fadeInUp}
        >
          <SectionHeader>
            <SectionTitle>About Nova Elite Homes</SectionTitle>
          </SectionHeader>
        </motion.div>

        <ContentSection>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.3 }}
            variants={slideInLeft}
          >
            <ImageContainer>
              <ImageWrapper>
                <Image src="/images/airbnb1.jpg" alt="Airbnb 1" />
              </ImageWrapper>
              <ImageWrapper>
                <Image src="/images/airbnb2.jpg" alt="Airbnb 2" />
              </ImageWrapper>
              <ImageWrapper>
                <Image src="/images/airbnb3.jpg" alt="Airbnb 3" />
              </ImageWrapper>
            </ImageContainer>
          </motion.div>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.3 }}
            variants={slideInRight}
          >
            <TextContent>
              <ContentText>
                At Nova Elite Homes Ltd, we specialise in providing comfortable and convenient accommodation services for working professionals and students in London. Whether you are relocating for a project, moving for studies, on a short-term assignment, or simply need a place to stay while in the city, we make the process seamless.
              </ContentText>
            </TextContent>
          </motion.div>
        </ContentSection>
      </ContentWrapper>
    </AboutUsSection>
  );
};

// Desktop Our Mission Component
const DesktopOurMission = () => {
  return (
    <AboutUsSection>
      <ContentWrapper>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.3 }}
          variants={fadeInUp}
        >
          <SectionHeader>
            <SectionTitle>Our Mission</SectionTitle>
          </SectionHeader>
        </motion.div>

        <ContentSection>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.3 }}
            variants={slideInLeft}
          >
            <ImageContainer>
              <ImageWrapper>
                <Image src="/images/airbnb4.jpg" alt="Airbnb 4" />
              </ImageWrapper>
              <ImageWrapper>
                <Image src="/images/airbnb5.jpg" alt="Airbnb 5" />
              </ImageWrapper>
              <ImageWrapper>
                <Image src="/images/airbnb6.jpg" alt="Airbnb 6" />
              </ImageWrapper>
            </ImageContainer>
          </motion.div>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.3 }}
            variants={slideInRight}
          >
            <TextContent>
              <ContentText>
                Our journey began as a small company with a simple but powerful idea: to make finding quality accommodation in London easier for professionals and students. What started with just a handful of properties and a determination to do things differently has grown into a thriving service trusted by individuals and businesses across the city.
              </ContentText>
              <ContentText>
                Our director built Nova Elite Homes Ltd from the ground up. Having worked for years in property administration, she gained extensive experience in managing homes, coordinating with clients, and understanding exactly what people need when relocating or working in a busy city like London. With that expertise, she transformed her knowledge and passion into a business dedicated to providing excellent living spaces and outstanding service.
              </ContentText>
              <ContentText>
                From the very beginning, her focus has been on more than just providing accommodation. She envisioned creating a service where professionals and students could feel at home, supported, and cared for during their time in London. That same vision still guides us today.
              </ContentText>
              <ContentText>
                We take pride in looking after every property with care and attention, ensuring our clients feel comfortable and valued. Many of our homes are thoughtfully arranged to bring people together in shared living spaces, while still offering privacy and comfort. As we continue to grow, we remain true to the standards that shaped us from the start: integrity, reliability, and excellence in everything we do.
              </ContentText>
            </TextContent>
          </motion.div>
        </ContentSection>
      </ContentWrapper>
    </AboutUsSection>
  );
};

// Desktop Our Vision Component
const DesktopOurVision = () => {
  return (
    <AboutUsSection>
      <ContentWrapper>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.3 }}
          variants={fadeInUp}
        >
          <SectionHeader>
            <SectionTitle>Our Vision</SectionTitle>
          </SectionHeader>
        </motion.div>

        <ContentSection>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.3 }}
            variants={slideInLeft}
          >
            <ImageContainer>
              <ImageWrapper>
                <Image src="/images/airbnb7.jpg" alt="Airbnb 7" />
              </ImageWrapper>
              <ImageWrapper>
                <Image src="/images/airbnb8.jpg" alt="Airbnb 8" />
              </ImageWrapper>
              <ImageWrapper>
                <Image src="/images/airbnb9.jpg" alt="Airbnb 9" />
              </ImageWrapper>
            </ImageContainer>
          </motion.div>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.3 }}
            variants={slideInRight}
          >
            <TextContent>
              <ContentText>
                Our vision is to be the trusted choice for accommodation in London, offering living spaces that allow individuals and teams to thrive while carrying forward the determination, passion, and excellence on which Nova Elite Homes Ltd was built.
              </ContentText>
            </TextContent>
          </motion.div>
        </ContentSection>
      </ContentWrapper>
    </AboutUsSection>
  );
};

// Desktop Why Choose Us Component
const DesktopWhyChooseUs = () => {
  return (
    <AboutUsSection>
      <ContentWrapper>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.3 }}
          variants={fadeInUp}
        >
          <SectionHeader>
            <SectionTitle>Why Choose Us</SectionTitle>
          </SectionHeader>
        </motion.div>

        <ContentSection>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.3 }}
            variants={slideInLeft}
          >
            <ImageContainer>
              <ImageWrapper>
                <Image src="/images/airbnb10.png" alt="Airbnb 10" />
              </ImageWrapper>
              <ImageWrapper>
                <Image src="/images/airbnb11.png" alt="Airbnb 11" />
              </ImageWrapper>
              <ImageWrapper>
                <Image src="/images/airbnb12.png" alt="Airbnb 12" />
              </ImageWrapper>
            </ImageContainer>
          </motion.div>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.3 }}
            variants={slideInRight}
          >
            <TextContent>
              <ContentText>
                Choosing where to stay in London can be overwhelming, but with Nova Elite Homes Ltd, you can be confident that your accommodation needs are in safe hands. Here's why so many professionals, students, and companies choose us:
              </ContentText>
              <ContentText>
                <strong>Tailored Services:</strong> We understand that every client's needs are unique. Whether you're a student looking for a comfortable place near your university or a company seeking accommodation for staff, we adapt our services to suit you.
              </ContentText>
              <ContentText>
                <strong>Prime London Locations:</strong> Our growing selection of properties is located across London, giving you access to well connected, convenient, and safe areas of the city.
              </ContentText>
              <ContentText>
                <strong>Shared Living with Comfort:</strong> Our properties are designed to provide a balance between community and privacy, giving clients the chance to enjoy affordable, practical accommodation while still feeling at home.
              </ContentText>
              <ContentText>
                <strong>Quality and Care:</strong> We treat every property as if it were our own. From upkeep to comfort, we make sure each space is ready for you to feel at home from the moment you move in.
              </ContentText>
              <ContentText>
                <strong>Professional Support:</strong> Our friendly and experienced team is always on hand to assist you before, during, and after your stay. We believe in building lasting relationships, not just short-term bookings.
              </ContentText>
              <ContentText>
                <strong>Flexible Options:</strong> Whether you need short-term or long-term accommodation, we provide flexible arrangements to match your schedule and requirements.
              </ContentText>
              <ContentText>
                <strong>Trusted by Businesses and Individuals:</strong> Companies rely on us to house their staff, and individuals trust us with one of their most important needs: a home while in London.
              </ContentText>
              <ContentText>
                With Nova Elite Homes Ltd, accommodation isn't just about a place to stay. It's about peace of mind, reliability, and a service you can count on.
              </ContentText>
            </TextContent>
          </motion.div>
        </ContentSection>
      </ContentWrapper>
    </AboutUsSection>
  );
};

// Desktop Our Commitment to Excellence Component
const DesktopOurCommitment = () => {
  return (
    <AboutUsSection>
      <ContentWrapper>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.3 }}
          variants={fadeInUp}
        >
          <SectionHeader>
            <SectionTitle>Our Commitment to Excellence</SectionTitle>
          </SectionHeader>
        </motion.div>

        <ContentSection>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.3 }}
            variants={slideInLeft}
          >
            <ImageContainer>
              <ImageWrapper>
                <Image src="/images/airbnb13.png" alt="Airbnb 13" />
              </ImageWrapper>
              <ImageWrapper>
                <Image src="/images/airbnb14.png" alt="Airbnb 14" />
              </ImageWrapper>
              <ImageWrapper>
                <Image src="/images/airbnb15.jpg" alt="Airbnb 15" />
              </ImageWrapper>
            </ImageContainer>
          </motion.div>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.3 }}
            variants={slideInRight}
          >
            <TextContent>
              <ContentText>
                At Nova Elite Homes Ltd, our commitment to excellence is not just a promise; it's the foundation of everything we do. We believe that exceptional accommodation services require more than just providing a place to stay; they demand attention to detail, genuine care for our clients, and an unwavering dedication to quality.
              </ContentText>
              <ContentText>
                Our team is highly motivated and brings innovative ideas to every aspect of our service. We continuously seek ways to improve, adapt, and enhance the experience we provide to professionals and students in London. From the moment you first contact us to the day you move out, we ensure that every interaction reflects our commitment to your satisfaction and comfort.
              </ContentText>
              <ContentText>
                We understand that finding the right accommodation can be stressful, which is why we go above and beyond to make the process as smooth and worry-free as possible. Our properties are carefully selected and maintained to the highest standards, ensuring that you feel at home from the very first day.
              </ContentText>
              <ContentText>
                We take pride in our attention to detail, from the cleanliness of each space to the quality of the amenities provided. Our commitment extends beyond just the physical aspects of accommodation; we also focus on building lasting relationships with our clients.
              </ContentText>
              <ContentText>
                We believe that excellent service means being there when you need us, listening to your concerns, and working tirelessly to address any issues that may arise. This dedication to excellence has earned us the trust of countless professionals, students, and businesses across London, and we remain committed to maintaining and exceeding these high standards in everything we do.
              </ContentText>
            </TextContent>
          </motion.div>
        </ContentSection>
      </ContentWrapper>
    </AboutUsSection>
  );
};

function App() {
  const [selectedProperty, setSelectedProperty] = useState(null);

  return (
    <>
      <GlobalStyles />

      <Router>
        <Header />

        <Routes>
          <Route
            path="/"
            element={
              <>
                <HeroSection />
                <AboutUs />
                <DesktopOnlySection id="about">
                  <DesktopAboutUs />
                  <DesktopOurMission />
                  <DesktopOurVision />
                  <DesktopWhyChooseUs />
                  <DesktopOurCommitment />
                </DesktopOnlySection>
                <MobileOnlySection id="about-mobile">
                  <PropertiesSection />
                </MobileOnlySection>
                <MapSection setSelectedProperty={setSelectedProperty}/>
                <ContactSection id="contact" />
              </>
            }
          />

          <Route path="/privacy-policy" element={<PrivacyPolicy />} />

          <Route path="/terms-of-service" element={<TermsOfService />} />
        </Routes>

        <Footer />
      </Router>

      {selectedProperty && (
        <PropertyModal property={selectedProperty} onClose={() => setSelectedProperty(null)} />
      )}
    </>
  );
}

export default App;